import React, { createContext, useContext, useEffect, useState } from "react";
import { signInWithGooglePopup, isGoogleConfigured } from "../lib/googleAuth";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("dplus_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (user) localStorage.setItem("dplus_user", JSON.stringify(user));
    else localStorage.removeItem("dplus_user");
  }, [user]);

  // MOCK: any email/password accepted; account stored locally
  const signIn = async ({ email, password }) => {
    if (!email || !password) throw new Error("Email and password required");
    const accounts = JSON.parse(localStorage.getItem("dplus_accounts") || "{}");
    const existing = accounts[email.toLowerCase()];
    if (existing && existing.password !== password) {
      throw new Error("Incorrect password");
    }
    const acct = existing || {
      email,
      password,
      firstName: email.split("@")[0].split(".")[0] || "Friend",
      lastName: "",
      phone: "",
      createdAt: new Date().toISOString(),
    };
    if (!existing) {
      accounts[email.toLowerCase()] = acct;
      localStorage.setItem("dplus_accounts", JSON.stringify(accounts));
    }
    const session = { ...acct };
    delete session.password;
    setUser(session);
    return session;
  };

  const signUp = async ({ firstName, lastName, email, password, phone }) => {
    if (!email || !password || !firstName) throw new Error("Please fill all required fields");
    const accounts = JSON.parse(localStorage.getItem("dplus_accounts") || "{}");
    if (accounts[email.toLowerCase()]) throw new Error("An account with this email already exists");
    const acct = {
      email,
      password,
      firstName,
      lastName: lastName || "",
      phone: phone || "",
      createdAt: new Date().toISOString(),
    };
    accounts[email.toLowerCase()] = acct;
    localStorage.setItem("dplus_accounts", JSON.stringify(accounts));
    const session = { ...acct };
    delete session.password;
    setUser(session);
    return session;
  };

  const signInWithGoogle = async () => {
    if (isGoogleConfigured()) {
      // Real Google Identity Services flow
      const profile = await signInWithGooglePopup();
      // profile: { sub, email, name, given_name, family_name, picture, email_verified }
      const acct = {
        email: profile.email,
        firstName: profile.given_name || (profile.name || "").split(" ")[0] || "Friend",
        lastName: profile.family_name || (profile.name || "").split(" ").slice(1).join(" "),
        picture: profile.picture,
        phone: "",
        createdAt: new Date().toISOString(),
        provider: "google",
        providerId: profile.sub,
      };
      // Also remember the account so the user can revisit
      const accounts = JSON.parse(localStorage.getItem("dplus_accounts") || "{}");
      accounts[acct.email.toLowerCase()] = { ...accounts[acct.email.toLowerCase()], ...acct };
      localStorage.setItem("dplus_accounts", JSON.stringify(accounts));
      setUser(acct);
      return acct;
    }
    // Fallback: MOCK Google sign-in (used until REACT_APP_GOOGLE_CLIENT_ID is set)
    const acct = {
      email: "demo.user@gmail.com",
      firstName: "Demo",
      lastName: "User",
      phone: "",
      createdAt: new Date().toISOString(),
      provider: "google-mock",
    };
    setUser(acct);
    return acct;
  };

  const signOut = () => setUser(null);

  const updateProfile = (patch) => {
    setUser((u) => ({ ...u, ...patch }));
    const accounts = JSON.parse(localStorage.getItem("dplus_accounts") || "{}");
    if (user?.email && accounts[user.email.toLowerCase()]) {
      accounts[user.email.toLowerCase()] = { ...accounts[user.email.toLowerCase()], ...patch };
      localStorage.setItem("dplus_accounts", JSON.stringify(accounts));
    }
  };

  return (
    <AuthContext.Provider value={{ user, signIn, signUp, signInWithGoogle, signOut, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
